import { Injectable } from '@angular/core';
import { ApiPathType } from 'api/api-models.js';
import { AppSetupApi } from 'api/app-setup/app-setup-api.js';
import { ImageGalleryApi } from 'api/image-gallery/image-gallery-api.js';
import { AppDataService } from 'lib/app-data.service.js';
import { CosmosDb } from 'lib/cosmos/cosmosDb.js';
import { IFileFormFieldCropOptions } from 'lib/ui/form/fields/file-form-field.component.js';
import { ITable, ITableAvatar } from 'lib/ui/table/index.js';
import { Observable, map, of } from 'rxjs';
import { HttpService } from '../http/http.service.js';
import {
	ISharedData,
	ITypePageCardAvatarUpload,
	ITypePageCardTitleConfig
} from '../pages/type-routes/type-route.models.js';
import { ItemType } from './core.models.js';

@Injectable({ providedIn: 'root' })
export class CoreAppService {
	private imageGalleriesInitStarted: boolean = false;
	private readonly apiTypeTransform: Record<CosmosDb.CosmosContainerType, ApiPathType> = {
		Analytics: 'analytics',
		Appointment: 'appointment',
		AppSetup: 'appSetup',
		Chat: 'chat',
		Diary: 'diary',
		Diet: 'diet',
		Exercise: 'exercise',
		Feed: 'feed',
		Food: 'food',
		Helpdesk: 'helpdesk',
		ImageGallery: 'imageGallery',
		Log: 'log',
		Package: 'package',
		ProgressType: 'progressType',
		PublicMessage: 'publicMessage',
		Schedule: 'schedule',
		Session: 'session',
		Task: 'task',
		User: 'user',
		Workout: 'workout',
		GenAi: 'genai',
		Snippet: 'snippet'
	};

	public readonly defaultSquareImageCrop: IFileFormFieldCropOptions = {
		aspectRatio: 'square',
		resizeToWidth: 250
	};

	constructor(private readonly httpService: HttpService, private readonly appDataService: AppDataService) {}

	initAppContext(): Observable<AppSetupApi.IAppContext> {
		return this.httpService
			.get<AppSetupApi.IAppContext>((builder) => {
				return builder.withCustomApiRoute('appSetup', 'getAppContext').build();
			})
			.pipe(
				map((response) => {
					this.appDataService.appContext.set(response.data);
					return response.data;
				})
			);
	}

	initImageGalleries(): Observable<void> {
		if (this.imageGalleriesInitStarted) {
			return of(undefined);
		}

		this.imageGalleriesInitStarted = true;

		return this.httpService
			.get<ImageGalleryApi.IImageGalleries>((builder) => {
				return builder.withCustomApiRoute('imageGallery', 'getImageGalleries').build();
			})
			.pipe(
				map((response) => {
					this.appDataService.imageGalleries.set(response.data);
				})
			);
	}

	getTitleCardConfig<TSharedData>(data: {
		item: CosmosDb.ICosmosItem;
		updateSharedData: (title: string, sharedData: ISharedData<TSharedData>) => ISharedData<TSharedData>;
	}): ITypePageCardTitleConfig<TSharedData> {
		return {
			updateSharedData: data.updateSharedData,
			value: data.item.system.name,
			save: (title) => {
				return this.httpService
					.patch({
						type: this.apiTypeTransform[data.item.system.type],
						patchModel: {
							id: data.item.id,
							name: title
						}
					})
					.pipe(map((response) => {}));
			}
		};
	}

	getAvatarCardUploadConfig<TCosmosItem extends CosmosDb.ICosmosItem, TSharedData>(data: {
		type: ApiPathType;
		existingAvatar: CosmosDb.ICosmosFileReference | undefined;
		patchModel: (file: CosmosDb.ICosmosFileReference | undefined) => CosmosDb.PatchItem<TCosmosItem>;
		updateSharedData: (
			file: CosmosDb.ICosmosFileReference | undefined,
			sharedData: ISharedData<TSharedData>
		) => ISharedData<TSharedData>;
		customSave?: (file: CosmosDb.ICosmosFileReference | undefined) => Observable<void>;
	}): ITypePageCardAvatarUpload<TSharedData> {
		return {
			avatarFile: data.existingAvatar,
			updateSharedData: data.updateSharedData,
			imageCrop: this.defaultSquareImageCrop,
			fileUpload: (file) => {
				return this.httpService
					.uploadFile({
						file: file,
						type: data.type
					})
					.pipe(
						map((response) => {
							return response.data;
						})
					);
			},
			save: (file) => {
				if (data.customSave) {
					return data.customSave(file);
				}
				return this.httpService
					.patch<TCosmosItem>({
						type: data.type,
						patchModel: data.patchModel(file)
					})
					.pipe(map((response) => {}));
			}
		};
	}

	getGlobalTableConfig<TCosmosItem extends CosmosDb.ICosmosItem>(data: {
		items: readonly TCosmosItem[];
		itemType: ItemType;
		subtitle?: (item: TCosmosItem) => string | undefined;
		avatar?: (item: TCosmosItem) => ITableAvatar;
	}): ITable<TCosmosItem> {
		const avatarFunc = data.avatar;

		return {
			itemType: data.itemType,
			search: {
				type: 'local'
			},
			noItemsText: {
				noItems: `There are no default items`,
				noFilteredItems: `No items match your filter`
			},
			items: data.items,
			id: (item) => item.id,
			subtitle: (item) => {
				return data.subtitle?.(item);
			},
			avatar: avatarFunc
				? (item) => {
						return avatarFunc(item);
				  }
				: undefined,
			title: {
				title: 'Name',
				value: (item) => item.system.name
			}
		};
	}
}
